import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { SidePageWrapper } from "components/layout/sidePageWrapper/SidePageWrapper";
import {
  QuickPayBar,
  QuickPayStep,
} from "components/pages/quickPay/QuickPayBar";
import { NonValidatedForm } from "./NonValidatedForm";
import { Form, FormState } from "components/common/form/Form";

import { useRootDispatch, useRootSelector } from "store/hooks";
import { useRequestDispatch } from "store/requestHooks";

import { fetchAccounts, FetchAccounts } from "store/account/account-action";
import { resetAccounts } from "store/account/account-slice";
import {
  AccountInfo as AccountInformation,
  AccountQueryParameter,
  FetchAccountInfoReqDTO,
} from "store/account/account-state";

import classes from "./AccountInfo.module.scss";
import { QuickPayType, TrackingProperties } from "store/config";
import { setPaymentData } from "store/payments/payments-slice";
import { getSearchParamValue } from "utility";
import { useAddNotification } from "hooks";
import { clearAllNotifications } from "store/notification/notification-slice";
import {
  ACCOUNT_INFORMATION_ID_QUERY_STRING_PARAM,
  NONCE_QUERY_STRING_PARAM,
  requestPayTrackingProperties,
} from "constants/request-pay";

const paramToPropertyMap: Record<string, string> = {
  id: ACCOUNT_INFORMATION_ID_QUERY_STRING_PARAM,
  nonce: NONCE_QUERY_STRING_PARAM,
};

const hasAllQueryParams = (
  queryParams: URLSearchParams,
  requiredKeys: string[],
): boolean => {
  const lcRequiredKeys = requiredKeys.map((key) => key.toLowerCase());
  return lcRequiredKeys.every((key) => {
    const value = Array.from(queryParams.keys()).find(
      (k) => k.toLowerCase() === key,
    );
    return (
      value !== undefined &&
      queryParams.get(value) !== null &&
      queryParams.get(value)!.trim() !== ""
    );
  });
};

const buildAccountQueryArray = (
  queryParams: URLSearchParams,
  trackingProperties: TrackingProperties[],
): AccountQueryParameter[] => {
  const entries = Array.from(queryParams.entries());

  return entries
    .map(([key, value]) => {
      const lcKey = key.toLowerCase();
      const propertyName = paramToPropertyMap[lcKey];

      const trackingProperty = trackingProperties.find(
        (prop) =>
          prop.propertyName.toLowerCase() === propertyName?.toLowerCase(),
      );

      return trackingProperty
        ? {
            trackingProperty,
            value,
          }
        : null;
    })
    .filter((query) => query !== null) as AccountQueryParameter[];
};

export const AccountInfo = () => {
  const { t } = useTranslation([
    "quickpay",
    "payment_final",
    "account_next",
    "payment_next",
    "common",
    "payment",
  ]);
  const { meta } = useRootSelector((state) => state.accounts);
  const { client, paymentCenter } = useRootSelector((state) => state.config);
  const requestDispatch = useRequestDispatch();
  const dispatch = useRootDispatch();
  const addNotification = useAddNotification();

  const [formState, setFormState] = useState<FormState>();

  const isNonValidated =
    paymentCenter.quickPayments.quickPayType === QuickPayType.NonValidated;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Reset any stale loading state that may have been persisted by redux-persist
    dispatch(resetAccounts());
    dispatch(clearAllNotifications());
    if (searchParams.size > 0) {
      try {
        if (hasAllQueryParams(searchParams, Object.keys(paramToPropertyMap))) {
          // Attempt to build the account query using the quick pay configuration.
          let values = buildAccountQueryArray(
            searchParams,
            paymentCenter.quickPayTrackingProperties,
          );
          // if no values were pulle for the quickpay tracking properties
          // use the default request pay ones.
          if (
            values.length === 0 ||
            values.length !== paymentCenter.quickPayTrackingProperties.length
          ) {
            values = buildAccountQueryArray(
              searchParams,
              requestPayTrackingProperties,
            );
          }

          const referenceValue = getSearchParamValue(
            "customerreferencenumber",
            searchParams,
          );
          if (referenceValue !== null) {
            dispatch(
              setPaymentData({ customerReferenceNumber: referenceValue }),
            );
          }
          accountQueryHandler(values);
        } else {
          navigate(`/${client.customerKey}`);
        }
      } catch (error) {
        console.error(error);
        navigate(`/${client.customerKey}`);
      }
    } else {
      if (paymentCenter.quickPayments.deepLinkingOnly) {
        navigate(`/${client.customerKey}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accountQueryHandler = (values: AccountQueryParameter[]) => {
    if (client.clientId) {
      const reqObj: FetchAccountInfoReqDTO = {
        clientId: client.clientId,
        accountQueryParameter: values,
      };
      requestDispatch<FetchAccounts, FetchAccountInfoReqDTO>(
        fetchAccounts,
        reqObj,
      )
        .unwrap()
        .then((res: AccountInformation[]) => {
          if (!res.some((account) => account.active === true)) {
            // Account matrix inacitve response (account lookup)
            addNotification({
              translatedMessages: [
                t("common:inactive-account", {
                  phone: client.primaryContactPhone,
                }),
              ],
              type: "ERROR",
              closable: true,
            });
          } else {
            dispatch(clearAllNotifications());
            navigate(`/${client.customerKey}/quick-payment`);
          }
        })
        .catch((e: any) => {
          console.error(e, "Error when looking up account.");
        });
    }
  };

  const makePaymentHandler = () => {
    let formIsValid = true;
    for (const single in formState) {
      if (!formState[single].ref.current.checkValidity()) {
        formState[single].ref.current.reportValidity();
        formIsValid = false;
      }
    }

    if (!formIsValid) {
      return;
    }

    const values: AccountQueryParameter[] =
      paymentCenter.quickPayTrackingProperties.map((single) => {
        return {
          trackingProperty: single,
          value: formState && formState[single.propertyName].value,
        };
      });

    accountQueryHandler(values);
  };

  const backButtonHandler = () => {
    navigate(`/${client.customerKey}`);
  };

  const buildForm = () => {
    if (paymentCenter.quickPayments.deepLinkingOnly) {
      return <></>;
    } else {
      return (
        <>
          <div className={classes.Loan_info}>
            {paymentCenter.quickPayTrackingProperties.length > 0 && (
              <Form
                fields={paymentCenter.quickPayTrackingProperties}
                formStateSetter={setFormState}
              />
            )}
          </div>
          <div className={classes.Button_container}>
            <ft-button
              onClick={backButtonHandler}
              class={[classes.Back, "outline-button"].join(" ")}
              label={t("quickpay:back_button")}
              outlined
            ></ft-button>
            <ft-button
              class={classes.Make_payment}
              label={t("quickpay:quickpay_make_payment")}
              unelevated
              onClick={makePaymentHandler}
            ></ft-button>
          </div>
        </>
      );
    }
  };

  return (
    <SidePageWrapper hideDisclaimeIcon={true} hideDisclaime={true}>
      {meta?.isLoading ? <ft-screen-loader></ft-screen-loader> : null}
      <article className={classes.Container}>
        <QuickPayBar
          progress="0.1"
          quickPayType={paymentCenter.quickPayments.quickPayType}
          titles={{
            loan: t("quickpay:loan"),
            information: t("quickpay:information"),
            payment: t("quickpay:payment"),
            review: t("quickpay:review"),
            complete: t("quickpay:complete"),
          }}
          activeStep={QuickPayStep.Info}
        />
        <section className={classes.Instructions}>
          <div>{t("quickpay:account_instructions").trim()}</div>
        </section>
        {isNonValidated ? <NonValidatedForm /> : buildForm()}
        <section className={classes.Disclosure}>
          <div className={classes.Disclosure_message}>
            {t("app:landing_page_disclosure")}
          </div>
        </section>
      </article>
    </SidePageWrapper>
  );
};
